import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { generateProposalHTML } from '@/lib/generateProposalHTML';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  MessageSquare, Send, Plus, Trash2, Save, Copy, Star, Download, FolderKanban,
  ChevronRight, ChevronLeft, ChevronDown, Pencil, Bot, User, AlertTriangle, Tag, Landmark, Eye, FileText, Wrench, Zap, CheckCircle, UserPlus, Compass
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const CATEGORIES = ['excavation','steel','plumbing','electrical','gas','gunite','tile','plaster','decking','equipment','utilities','covers','features','permits','misc','custom'];

export default function BidEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [bid, setBid] = useState(null);
  const [items, setItems] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [newItem, setNewItem] = useState({ category: 'misc', name: '', qty: 1, unit: 'EA', unit_cost: 0, markup_pct: 30, sell_price: '', notes: '' });
  const [editingItem, setEditingItem] = useState(null);
  const [financeEstimates, setFinanceEstimates] = useState(null);
  const [sendProposalOpen, setSendProposalOpen] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [proposalLink, setProposalLink] = useState('');
  const [proposals, setProposals] = useState([]);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistData, setChecklistData] = useState(null);
  const [checklistConfirmed, setChecklistConfirmed] = useState({});
  const [inviteHomeownerOpen, setInviteHomeownerOpen] = useState(false);
  const [hoForm, setHoForm] = useState({ name: '', email: '', phone: '' });
  const [hoCreated, setHoCreated] = useState(null);
  const chatEndRef = useRef(null);

  const loadFinance = async (total) => {
    if (total > 0) {
      try { const r = await api.post('/finance/calculate', { amount: total }); setFinanceEstimates(r.data); } catch {}
    }
  };

  const loadBid = async () => {
    const r = await api.get(`/bids/${id}?_t=${Date.now()}`);
    setBid(r.data);
    setItems(r.data.line_items || []);
    if (r.data.totals?.total_price > 0) loadFinance(r.data.totals.total_price);
  };

  useEffect(() => { loadBid(); }, [id]);
  useEffect(() => {
    if (aiOpen) api.get(`/ai/chat/${id}/history`).then(r => setChatHistory(r.data)).catch(() => {});
  }, [aiOpen, id]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatHistory]);

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput;
    setChatInput('');
    setChatHistory(prev => [...prev, { role: 'user', content: msg, created_at: new Date().toISOString() }]);
    setChatLoading(true);
    try {
      const r = await api.post('/ai/chat', { bid_id: id, message: msg });
      setChatHistory(prev => [...prev, { role: 'assistant', content: r.data.message, created_at: new Date().toISOString() }]);
      if (r.data.bid) setBid(r.data.bid);
      if (r.data.line_items) setItems(r.data.line_items);
    } catch { toast.error('AI chat error'); }
    setChatLoading(false);
  };

  const handleAddItem = async () => {
    const payload = { ...newItem, unit_cost: parseFloat(newItem.unit_cost) || 0, qty: parseFloat(newItem.qty) || 1,
      markup_pct: parseFloat(newItem.markup_pct) || 33, sell_price: newItem.sell_price ? parseFloat(newItem.sell_price) : null,
      is_allowance: newItem.is_allowance || false, allowance_amount: newItem.allowance_amount ? parseFloat(newItem.allowance_amount) : null,
      pricing_bucket: newItem.pricing_bucket || 'base' };
    if (editingItem) {
      await api.put(`/bids/${id}/line-items/${editingItem.id}`, payload);
      toast.success('Item updated');
    } else {
      await api.post(`/bids/${id}/line-items`, payload);
      toast.success('Item added');
    }
    setAddItemOpen(false);
    setEditingItem(null);
    setNewItem({ category: 'misc', name: '', qty: 1, unit: 'LOT', unit_cost: 0, markup_pct: 33, sell_price: '', is_allowance: false, allowance_amount: '', notes: '', pricing_bucket: 'base' });
    loadBid();
  };

  const handleDeleteItem = async (itemId) => {
    await api.delete(`/bids/${id}/line-items/${itemId}`);
    toast.success('Item removed');
    loadBid();
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setNewItem({ category: item.category, name: item.name, qty: item.qty, unit: item.unit,
      unit_cost: item.unit_cost, markup_pct: item.markup_pct, sell_price: item.sell_price || (item.unit_cost * (1 + item.markup_pct / 100)).toFixed(2),
      is_allowance: item.is_allowance || false, allowance_amount: item.allowance_amount || '',
      notes: item.notes || '', pricing_bucket: item.pricing_bucket || 'base' });
    setAddItemOpen(true);
  };

  const handleStatusChange = async (status) => {
    try {
      const r = await api.put(`/bids/${id}`, { status });
      if (r.data.requires_checklist) {
        setChecklistData(r.data.checklist);
        setChecklistConfirmed({});
        setChecklistOpen(true);
        return;
      }
      if (r.data.approval_status === 'pending') {
        toast.warning(r.data.approval_message || 'Admin approval required — margin too low');
        loadBid();
        return;
      }
      if (r.data.scope_warning) {
        toast.warning(r.data.scope_warning);
        loadBid();
        return;
      }
      toast.success(`Bid marked as ${status}`);
      loadBid();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleChecklistSend = async () => {
    try {
      const r = await api.put(`/bids/${id}`, { status: 'sent', checklist_confirmed: true });
      if (r.data.approval_status === 'pending') {
        toast.warning(r.data.approval_message || 'Admin approval required');
      } else if (r.data.scope_warning) {
        toast.warning(r.data.scope_warning);
      } else {
        toast.success('Bid sent!');
      }
      setChecklistOpen(false);
      loadBid();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleApproveBid = async (action) => {
    await api.post(`/bids/${id}/approve`, { action });
    toast.success(action === 'approve' ? 'Bid approved and sent!' : 'Bid approval rejected');
    loadBid();
  };

  const handleDuplicate = async () => {
    const r = await api.post(`/bids/${id}/duplicate`);
    toast.success('Bid duplicated');
    navigate(`/bids/${r.data.id}`);
  };

  const handleSaveTemplate = async () => {
    await api.post(`/bids/${id}/save-template`);
    toast.success('Saved as template');
    loadBid();
  };

  const handleConvertToProject = async () => {
    const r = await api.post('/projects', { bid_id: id, name: bid?.inputs?.customer_name || 'New Project' });
    toast.success('Project created!');
    navigate('/projects');
  };

  const handleSendProposal = async () => {
    try {
      const baseUrl = window.location.origin;
      const r = await api.post(`/bids/${id}/send-proposal`, { customer_email: customerEmail, base_url: baseUrl });
      setProposalLink(`${baseUrl}/p/${r.data.token}`);
      toast.success(r.data.email_sent ? 'Proposal sent to customer!' : 'Portal link generated!');
      setSendProposalOpen(false);
      loadBid();
      loadProposals();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send proposal');
    }
  };

  const loadProposals = async () => {
    try { const r = await api.get(`/bids/${id}/proposals`); setProposals(r.data); } catch {}
  };

  useEffect(() => { if (id) loadProposals(); }, [id]);

  // Single source of truth for fresh proposal data — recompute + fetch
  const getFreshProposalData = async () => {
    try { await api.post(`/bids/${id}/compute-totals`); } catch {}
    const r = await api.get(`/bids/${id}/proposal-data?_t=${Date.now()}`);
    return r.data;
  };

  const handleDownloadProposal = async () => {
    const data = await getFreshProposalData();
    const html = generateProposalHTML(data);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `proposal_${bid?.inputs?.customer_name || 'bid'}.html`; a.click();
    toast.success('Proposal downloaded');
  };

  const isInIframe = window.self !== window.top;

  const handleDownloadPDF = async () => {
    try { await api.post(`/bids/${id}/compute-totals`); } catch {}
    const token = localStorage.getItem('pp_token');
    const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/bids/${id}/proposal-pdf?token=${token}&mode=view&_t=${Date.now()}`;

    if (isInIframe) {
      // Preview mode: copy link + attempt new tab
      navigator.clipboard.writeText(pdfUrl).then(() => {
        toast.success('PDF link copied to clipboard!', {
          description: 'Paste in a new browser tab to view. If your ad blocker blocks the popup, use the copied link.',
          duration: 10000,
        });
      }).catch(() => {});
      window.open(pdfUrl, '_blank');
      return;
    }
    // Production mode: direct download
    const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/bids/${id}/proposal-pdf?token=${token}&mode=download&_t=${Date.now()}`;
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `HDH_Proposal_${(bid?.inputs?.customer_name || 'Pool').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('PDF downloading');
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const handlePreview = async () => {
    const data = await getFreshProposalData();
    setPreviewHtml(generateProposalHTML(data));
    setPreviewOpen(true);
  };

  const updateSchedule = async (idx, field, value) => {
    const schedule = [...(bid.payment_schedule || [])];
    schedule[idx] = { ...schedule[idx], [field]: field === 'pct' ? parseFloat(value) || 0 : value };
    await api.put(`/bids/${id}`, { payment_schedule: schedule });
    loadBid();
  };

  if (!bid) return <div className="p-8"><div className="h-96 bg-white rounded-xl animate-pulse" /></div>;

  const totals = bid.totals || {};
  const totalPct = (bid.payment_schedule || []).reduce((s, i) => s + (i.pct || 0), 0);
  const grouped = {};
  items.forEach(i => { if (!grouped[i.category]) grouped[i.category] = []; grouped[i.category].push(i); });

  return (
    <div className="flex h-[calc(100vh-3.5rem)]" data-testid="bid-editor-page">
      <div className={`flex-1 overflow-y-auto p-6 lg:p-8 transition-all ${aiOpen ? 'mr-[380px]' : ''}`}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-extrabold text-[#1e3a50]">{bid.inputs?.customer_name || 'Untitled Bid'}</h1>
                <Badge className={`${bid.status === 'won' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : bid.status === 'sent' ? 'bg-blue-50 text-blue-700 border-blue-200' : bid.status === 'lost' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-gray-100 text-gray-600'}`}>{bid.status}</Badge>
                <Badge className="bg-[#e4f0f6] text-[#2a6496] border-[#c8dce8] text-[10px]" data-testid="version-badge">{bid.version_label || `Draft V${bid.version || 1}`}</Badge>
                {bid.frozen && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Locked</Badge>}
              </div>
              <p className="text-sm text-[#6b8a9e]">{bid.inputs?.city} | {bid.inputs?.pool_type} {bid.inputs?.length}x{bid.inputs?.width}
                {bid.updated_at && <span className="ml-2 text-[10px] text-slate-400">Saved {new Date(bid.updated_at).toLocaleTimeString()}</span>}
              </p>
              {bid.scope_completeness_score !== undefined && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center gap-1.5">
                    <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${bid.scope_completeness_score >= 80 ? 'bg-emerald-500' : bid.scope_completeness_score >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${bid.scope_completeness_score}%` }} />
                    </div>
                    <span className={`text-[10px] font-bold font-mono ${bid.scope_completeness_score >= 80 ? 'text-emerald-600' : bid.scope_completeness_score >= 60 ? 'text-amber-600' : 'text-red-600'}`}>{bid.scope_completeness_score}% scope</span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={async () => {
                try {
                  await api.put(`/bids/${id}`, { inputs: bid.inputs });
                  await api.post(`/bids/${id}/compute-totals`);
                  toast.success('Saved');
                  loadBid();
                } catch { toast.error('Save failed'); }
              }} className="bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-wider text-xs rounded-sm" data-testid="save-bid-btn">
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
              <Button variant="outline" size="sm" onClick={() => setAiOpen(!aiOpen)} className="rounded-sm" data-testid="toggle-ai-btn">
                <MessageSquare className="w-4 h-4 mr-1" /> {aiOpen ? 'Close AI' : 'AI Assistant'}
              </Button>
              <Select value={bid.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-28 h-9" data-testid="bid-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={handleDuplicate} data-testid="duplicate-bid-btn"><Copy className="w-4 h-4" /></Button>
              {!bid.frozen && (
                <Button variant="outline" size="sm" className="text-[#4a9ac5] border-[#c8dce8]" onClick={async () => {
                  await api.post(`/bids/${id}/new-version`);
                  toast.success('New version created');
                  loadBid();
                }} data-testid="new-version-btn">
                  V{(bid.version || 1) + 1}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleSaveTemplate} data-testid="save-template-btn"><Star className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" onClick={handlePreview} className="text-sky-600 border-sky-200" data-testid="preview-bid-btn">
                <Eye className="w-4 h-4 mr-1" /> Preview
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="text-emerald-600 border-emerald-200" data-testid="download-pdf-btn">
                <Download className="w-4 h-4 mr-1" /> PDF
              </Button>
              <Button variant="outline" size="sm" className="text-amber-600 border-amber-200" onClick={() => navigate(`/bids/${id}/edit`)} data-testid="edit-proposal-btn">
                <Pencil className="w-4 h-4 mr-1" /> Edit Proposal
              </Button>
              <Button variant="outline" size="sm" className="text-violet-600 border-violet-200" onClick={async () => {
                try {
                  const r = await api.post(`/plans/from-proposal/${id}`);
                  if (r.data.requires_choice) {
                    const plans = r.data.existing_plans || [];
                    const choice = window.confirm(`This proposal has ${plans.length} existing plan(s).\n\nOK = Open latest plan\nCancel = Create new revision`);
                    if (choice) {
                      navigate(`/plans/${plans[0].id}`);
                    } else {
                      const rev = await api.post(`/plans/from-proposal/${id}`, { action: 'new_revision' });
                      navigate(`/plans/${rev.data.id}`);
                      toast.success('Revision plan created');
                    }
                  } else {
                    navigate(`/plans/${r.data.id}`);
                    toast.success('Draft plan created from proposal');
                  }
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }} data-testid="create-plan-btn">
                <Compass className="w-4 h-4 mr-1" /> Plan
              </Button>
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => { setCustomerEmail(bid.inputs?.email || ''); setSendProposalOpen(true); }}
                data-testid="send-proposal-btn">
                <Send className="w-4 h-4 mr-1" /> Send Proposal
              </Button>
              {bid.status === 'won' && (
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={handleConvertToProject} data-testid="convert-to-project-btn">
                  <FolderKanban className="w-4 h-4 mr-1" /> Create Project
                </Button>
              )}
              {bid.status === 'won' && (
                <Button size="sm" variant="outline" className="border-[#4a9ac5] text-[#2a6496]" onClick={() => setInviteHomeownerOpen(true)} data-testid="invite-homeowner-btn">
                  <UserPlus className="w-4 h-4 mr-1" /> Invite Homeowner
                </Button>
              )}
            </div>
          </div>

          {/* Frozen Banner */}
          {bid.frozen && !isAdmin && (
            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6" data-testid="frozen-banner">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-amber-800">Estimate Accepted & Locked</p>
                <p className="text-xs text-amber-600">This bid is frozen. Scope changes require a formal Change Order through the project.</p>
              </div>
            </div>
          )}
          {bid.frozen && isAdmin && (
            <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6" data-testid="frozen-banner-admin">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-amber-800">Estimate Locked</p>
                  <p className="text-xs text-amber-600">Admin can unlock this bid for revisions.</p>
                </div>
              </div>
              <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 rounded-sm font-bold text-xs uppercase tracking-wider" onClick={async () => {
                try {
                  await api.put(`/bids/${id}`, { status: 'draft' });
                  toast.success('Bid unlocked — status set to Draft');
                  loadBid();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
              }} data-testid="unlock-bid-btn">
                Unlock for Editing
              </Button>
            </div>
          )}

          {/* Margin Warning */}
          {totals.margin_warning && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl mb-6 animate-fade-in" data-testid="margin-warning">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-red-700">Margin Below Minimum ({totals.min_margin_pct}%)</p>
                <p className="text-xs text-red-600">Current gross margin is {totals.gross_margin_pct}%. Review pricing before sending.</p>
              </div>
            </div>
          )}

          {/* Version Timeline */}
          {(bid.version_history || []).length > 0 && (
            <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1" data-testid="version-timeline">
              {(bid.version_history || []).map((vh, i) => (
                <div key={i} className="flex items-center gap-2 flex-shrink-0">
                  <div className={`px-3 py-1.5 rounded-lg text-[10px] font-bold ${
                    vh.label === 'Accepted' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                    i === (bid.version_history || []).length - 1 ? 'bg-[#4a9ac5]/10 text-[#2a6496] border border-[#c8dce8]' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {vh.label}
                    <span className="block text-[8px] font-medium opacity-60">{new Date(vh.created_at).toLocaleDateString()}</span>
                  </div>
                  {i < (bid.version_history || []).length - 1 && <div className="w-6 h-[2px] bg-gray-200 flex-shrink-0" />}
                </div>
              ))}
            </div>
          )}

          {/* Pricing Summary — 3-Step Breakdown */}
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-6">
            <CardContent className="p-4 space-y-4">
              {/* Section 1: Base Pool */}
              <div className="grid grid-cols-3 gap-4 p-3 bg-slate-50 rounded-lg">
                <div className="col-span-3 mb-1"><p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Base Pool</p></div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Cost</p>
                  <p className="text-lg font-bold font-mono text-[#1e3a50]">${(totals.base_cost || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Sell</p>
                  <p className="text-lg font-bold font-mono text-[#2a6496]">${(totals.base_sell || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Margin</p>
                  {(() => {
                    const c = totals.base_cost || 0;
                    const s = totals.base_sell || 0;
                    const m = s > 0 ? Math.round((s - c) / s * 100) : 0;
                    return <p className={`text-lg font-bold font-mono ${m < 20 ? 'text-red-600' : 'text-emerald-700'}`}>{m}%</p>;
                  })()}
                </div>
              </div>

              {/* Section 2: Base + Add-Ons */}
              <div className="grid grid-cols-3 gap-4 p-3 bg-violet-50/50 rounded-lg">
                <div className="col-span-3 mb-1"><p className="text-[11px] font-bold text-violet-600 uppercase tracking-wider">Base + Add-Ons</p></div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Cost</p>
                  <p className="text-lg font-bold font-mono text-[#1e3a50]">${((totals.base_cost || 0) + (totals.addon_cost || 0)).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Sell</p>
                  <p className="text-lg font-bold font-mono text-[#2a6496]">${((totals.base_sell || 0) + (totals.addon_sell || 0)).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Margin</p>
                  {(() => {
                    const c = (totals.base_cost || 0) + (totals.addon_cost || 0);
                    const s = (totals.base_sell || 0) + (totals.addon_sell || 0);
                    const m = s > 0 ? Math.round((s - c) / s * 100) : 0;
                    return <p className={`text-lg font-bold font-mono ${m < 20 ? 'text-red-600' : 'text-emerald-700'}`}>{m}%</p>;
                  })()}
                </div>
              </div>

              {/* Section 3: Final (Base + Add-Ons + Options) */}
              <div className="grid grid-cols-3 gap-4 p-3 bg-[#f5f9fb] rounded-lg border border-[#d4e6ef]/50">
                <div className="col-span-3 mb-1"><p className="text-[11px] font-bold text-[#2a6496] uppercase tracking-wider">Final Project</p></div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Cost</p>
                  <p className="text-lg font-bold font-mono text-[#1e3a50]">${Math.round(totals.direct_cost || totals.total_hard_cost || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Sell</p>
                  <p className="text-lg font-bold font-mono text-[#2a6496]">${Math.round(totals.subtotal_before_discount || totals.total_price || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Margin</p>
                  <p className={`text-lg font-bold font-mono ${(totals.gross_margin_pct || 0) < 20 ? 'text-red-600' : 'text-emerald-700'}`}>{totals.gross_margin_pct || 0}%</p>
                </div>
                {(totals.discount_amount || 0) > 0 && (
                  <div>
                    <p className="text-[10px] text-red-500 uppercase">Discount</p>
                    <p className="text-lg font-bold font-mono text-red-600">-${(totals.discount_amount || 0).toLocaleString()}</p>
                  </div>
                )}
                <div className="col-span-2 bg-white rounded-md p-2 -m-1">
                  <p className="text-[10px] text-[#2a6496] uppercase font-bold">Final Sell Price</p>
                  <p className="text-xl font-extrabold font-mono text-[#0F1D32]">${(totals.total_price || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Equipment Package Summary */}
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-4">
            <CardContent className="p-4">
              {/* Discount Section */}
              <DiscountControls bid={bid} id={id} totals={totals} loadBid={loadBid} />
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-[#4a9ac5]/10 flex items-center justify-center"><Wrench className="w-3.5 h-3.5 text-[#2a6496]" /></div>
                <p className="text-sm font-bold text-[#1e3a50]">Equipment Package</p>
                <Badge className="bg-[#4a9ac5]/15 text-[#2a6496] text-[9px] font-bold">PENTAIR PREMIUM STANDARD</Badge>
              </div>
              <p className="text-xs font-medium text-[#4a6a7e] mb-2">HDH standard equipment is a full Pentair package — variable-speed pump, cartridge filter, gas heater, salt chlorination, and automation with app control.</p>
              <div className="flex flex-wrap gap-1.5">
                {items.filter(i => i.category === 'equipment').map(i => (
                  <Badge key={i.id} className="bg-[#f5f9fb] text-[#2a6496] border border-[#d4e6ef] text-[9px]">{i.name.replace('Pentair ', '')} x{i.qty}</Badge>
                ))}
                {items.filter(i => i.category === 'equipment').length === 0 && <span className="text-[10px] text-[#6b8a9e]">No equipment items — select a package in the wizard</span>}
              </div>
            </CardContent>
          </Card>

          {/* Utility Runs Summary */}
          {items.some(i => i.category === 'utilities') && (
            <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-6">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center"><Zap className="w-3.5 h-3.5 text-emerald-600" /></div>
                  <p className="text-sm font-bold text-[#1e3a50]">Utility Runs</p>
                  <Badge className="bg-emerald-50 text-emerald-700 text-[9px] font-bold">INCLUDED</Badge>
                </div>
                <p className="text-xs font-medium text-[#4a6a7e] mb-3">HDH includes gas and electric runs from the equipment pad to your home's connection point — most builders stop at the pad.</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {items.filter(i => i.category === 'utilities').map(i => {
                    const sp = i.sell_price || i.unit_cost * (1 + i.markup_pct / 100);
                    return (
                      <div key={i.id} className="p-2 bg-[#f5f9fb] rounded-lg">
                        <p className="text-[10px] font-bold text-[#2a4a60]">{i.name.replace('Electric Run - ', '').replace('Gas Run - ', '').replace('Electrical ', '').replace('(200A)', '').trim()}</p>
                        <p className="text-xs font-bold font-mono text-[#1e3a50]">{i.qty > 1 ? `${i.qty} ${i.unit} — ` : ''}${(sp * i.qty).toLocaleString(undefined, {style:'currency',currency:'USD',minimumFractionDigits:0})}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Approval Banner */}
          {bid.approval_status === 'pending' && (
            <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6 animate-fade-in" data-testid="approval-pending-banner">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-sm font-bold text-amber-800">Pending Admin Approval</p>
                  <p className="text-xs text-amber-600">Margin is below minimum. Requires admin approval to send.</p>
                </div>
              </div>
              {user?.role === 'admin' && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-red-300 text-red-600" onClick={() => handleApproveBid('reject')} data-testid="reject-bid-btn">Reject</Button>
                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => handleApproveBid('approve')} data-testid="approve-bid-btn">Approve & Send</Button>
                </div>
              )}
            </div>
          )}

          {/* Design Preview Images */}
          {/* Design Package + Materials/Style + Prompt Assembly */}
          <DesignPackagePanel bid={bid} bidId={id} loadBid={loadBid} />
          <MaterialsStyleInputs bid={bid} bidId={id} loadBid={loadBid} />
          <RenderPromptBuilder bid={bid} bidId={id} loadBid={loadBid} />
          <RenderWorkflow bid={bid} bidId={id} loadBid={loadBid} />

          {/* Finance Estimates */}
          {financeEstimates && financeEstimates.options.length > 0 && (
            <Card className="border-sky-200 bg-sky-50/30 mb-6">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Landmark className="w-4 h-4 text-sky-600" />
                    <p className="text-sm font-bold text-sky-800">Financing Available</p>
                  </div>
                  {financeEstimates.lowest_monthly > 0 && (
                    <div className="px-3 py-1 bg-white rounded-lg border border-sky-200">
                      <p className="text-xs text-sky-600 font-semibold">As low as <span className="text-lg font-extrabold text-sky-700 font-mono">${financeEstimates.lowest_monthly.toLocaleString()}</span>/mo</p>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {financeEstimates.options.filter(o => o.monthly_payment > 0).slice(0, 4).map(opt => (
                    <div key={opt.program_id} className="p-2.5 bg-white rounded-lg border border-slate-100 text-center">
                      <p className="text-lg font-bold font-mono text-slate-800">${opt.monthly_payment.toLocaleString()}</p>
                      <p className="text-[10px] text-slate-500">{opt.program_name}</p>
                      <p className="text-[10px] text-slate-400">{opt.rate}% | {opt.term_years}yr</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Line Items — Grouped by Pricing Bucket */}
          {(() => {
            const bucketOrder = [
              { key: 'base', label: 'Base Pool Build', color: 'bg-emerald-600', bgHeader: 'bg-emerald-50 border-emerald-200' },
              { key: 'selected_addon', label: 'Included Add-Ons', color: 'bg-violet-600', bgHeader: 'bg-violet-50 border-violet-200' },
              { key: 'available_option', label: 'Available Options (Not in Total)', color: 'bg-sky-600', bgHeader: 'bg-sky-50 border-sky-200' },
              { key: 'hidden', label: 'Hidden from Proposal', color: 'bg-slate-500', bgHeader: 'bg-slate-100 border-slate-200' },
            ];
            const bucketItems = {};
            items.forEach(i => {
              const b = i.pricing_bucket || 'base';
              if (!bucketItems[b]) bucketItems[b] = [];
              bucketItems[b].push(i);
            });
            return bucketOrder.filter(b => (bucketItems[b.key] || []).length > 0).map(bucket => {
              const bItems = bucketItems[bucket.key] || [];
              const bGrouped = {};
              bItems.forEach(i => { if (!bGrouped[i.category]) bGrouped[i.category] = []; bGrouped[i.category].push(i); });
              const bucketTotal = bItems.reduce((s, i) => s + (i.sell_price || i.unit_cost * (1 + i.markup_pct / 100)) * i.qty, 0);
              return (
                <Card key={bucket.key} className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-4" data-testid={`bucket-section-${bucket.key}`}>
                  <div className={`px-4 py-3 ${bucket.bgHeader} border rounded-t-lg flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${bucket.color}`} />
                      <span className="text-sm font-bold text-slate-800">{bucket.label}</span>
                      <span className="text-xs text-slate-500">({bItems.length})</span>
                    </div>
                    <span className="text-sm font-bold font-mono text-slate-800">${bucketTotal.toLocaleString(undefined, { minimumFractionDigits: 0 })}</span>
                  </div>
                  <CardContent className="p-0">
                    {Object.entries(bGrouped).map(([cat, catItems]) => (
                      <Collapsible key={cat} defaultOpen>
                        <CollapsibleTrigger className="w-full px-4 py-2 bg-[#eef5f9] border-y border-[#d4e6ef]/50 flex items-center justify-between hover:bg-[#e4f0f6] transition-colors cursor-pointer">
                          <div className="flex items-center gap-2">
                            <ChevronDown className="w-3 h-3 text-[#6b8a9e]" />
                            <span className="text-[10px] font-bold text-[#2a6496] uppercase tracking-wider">{cat}</span>
                            <span className="text-[10px] text-[#6b8a9e]">({catItems.length})</span>
                          </div>
                          <span className="text-[10px] font-bold font-mono text-[#2a4a60]">
                            ${catItems.reduce((s, i) => s + (i.sell_price || i.unit_cost * (1 + i.markup_pct / 100)) * i.qty, 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                          </span>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                        <Table>
                          <TableBody>
                            {catItems.map(item => {
                              const sp = item.sell_price || item.unit_cost * (1 + item.markup_pct / 100);
                              return (
                                <TableRow key={item.id} className="hover:bg-sky-50/30" data-testid={`line-item-${item.id}`}>
                                  <TableCell className="w-1/3">
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-medium text-sm text-slate-800">{item.name}</span>
                                      {item.is_allowance && <Badge className="bg-amber-100 text-amber-700 text-[8px] px-1 py-0">ALLOW</Badge>}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <select
                                      value={item.pricing_bucket || 'base'}
                                      onChange={async (e) => {
                                        await api.put(`/bids/${id}/line-items/${item.id}`, { pricing_bucket: e.target.value });
                                        loadBid();
                                      }}
                                      className={`text-[10px] font-semibold rounded px-1.5 py-0.5 border-0 cursor-pointer ${
                                        item.pricing_bucket === 'selected_addon' ? 'bg-violet-100 text-violet-700' :
                                        item.pricing_bucket === 'available_option' ? 'bg-sky-100 text-sky-700' :
                                        item.pricing_bucket === 'hidden' ? 'bg-slate-200 text-slate-500' :
                                        'bg-emerald-50 text-emerald-700'
                                      }`}
                                      data-testid={`bucket-select-${item.id}`}
                                    >
                                      <option value="base">Base</option>
                                      <option value="selected_addon">Included</option>
                                      <option value="available_option">Optional</option>
                                      <option value="hidden">Hidden</option>
                                    </select>
                                  </TableCell>
                                  <TableCell className="font-mono text-xs text-slate-500">{item.qty} {item.unit}</TableCell>
                                  <TableCell className="font-mono text-xs text-right">${item.unit_cost.toLocaleString()}</TableCell>
                                  <TableCell className="font-mono text-xs text-right">{item.markup_pct}%</TableCell>
                                  <TableCell className="font-mono text-xs text-right font-semibold">${(sp * item.qty).toLocaleString()}</TableCell>
                                  <TableCell className="w-20">
                                    <div className="flex gap-0.5">
                                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEditItem(item)}><Pencil className="w-3 h-3 text-slate-400" /></Button>
                                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteItem(item.id)}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                        </CollapsibleContent>
                      </Collapsible>
                    ))}
                  </CardContent>
                </Card>
              );
            });
          })()}
          <div className="flex justify-end mb-6">
            <Button size="sm" className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={() => { setEditingItem(null); setNewItem({ category: 'misc', name: '', qty: 1, unit: 'LOT', unit_cost: 0, markup_pct: 33, sell_price: '', is_allowance: false, allowance_amount: '', notes: '', pricing_bucket: 'base' }); setAddItemOpen(true); }}
              data-testid="add-line-item-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Item</Button>
          </div>

          {/* Payment Schedule */}
          <Card className="border-slate-100 mb-6">
            <CardHeader className="pb-2"><CardTitle className="text-base">Payment Schedule</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(bid.payment_schedule || []).map((s, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-sm text-slate-700 w-32">{s.name}</span>
                    <Input type="number" className="w-20 h-8 font-mono text-sm" value={s.pct}
                      onChange={e => updateSchedule(idx, 'pct', e.target.value)} data-testid={`payment-pct-${idx}`} />
                    <span className="text-xs text-slate-500">%</span>
                    <span className="font-mono text-sm text-slate-800">${(s.amount || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <p className={`text-xs font-mono mt-3 ${Math.abs(totalPct - 100) < 0.01 ? 'text-emerald-600' : 'text-red-500'}`}>
                Total: {totalPct}% {Math.abs(totalPct - 100) >= 0.01 ? '(must equal 100%)' : ''}
              </p>
            </CardContent>
          </Card>

          {/* Notes & Additional Items (All Users) */}
          <Card className="border-border mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Notes & Additional Promises</CardTitle>
              <p className="text-[10px] text-muted-foreground">Add any commitments, extras, or notes for this bid. Visible to the team.</p>
            </CardHeader>
            <CardContent>
              <Textarea
                value={bid.inputs?.team_notes || ''}
                onChange={async (e) => {
                  const newInputs = { ...bid.inputs, team_notes: e.target.value };
                  await api.put(`/bids/${id}`, { inputs: newInputs });
                  loadBid();
                }}
                placeholder="e.g. Promised free pool cover if signed by March 15, Include extra LED light at no charge, Customer wants blue glass tile samples..."
                rows={4}
                className="text-sm"
                data-testid="bid-team-notes"
              />
            </CardContent>
          </Card>

          {/* Proposal Settings (Admin Only) */}
          {/* Proposal History */}
          {proposals.length > 0 && (
            <Card className="border-border mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Proposal History</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {proposals.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-muted rounded-lg" data-testid={`proposal-${p.id}`}>
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-sky-500" />
                      <div>
                        <p className="text-sm font-medium">{p.customer_name || 'Customer'}</p>
                        <p className="text-[10px] text-muted-foreground">{new Date(p.created_at).toLocaleDateString()} | {p.customer_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[10px] ${p.status === 'signed' ? 'bg-emerald-100 text-emerald-700' : p.status === 'declined' ? 'bg-red-100 text-red-700' : p.status === 'viewed' ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-600'}`}>{p.status}</Badge>
                      <Button variant="ghost" size="sm" className="h-7 text-xs"
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/p/${p.token}`); toast.success('Link copied!'); }}>
                        Copy Link
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Proposal Link Banner */}
          {proposalLink && (
            <Card className="border-emerald-200 bg-emerald-50 mb-6">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-emerald-800">Proposal Link Generated</p>
                  <p className="text-xs text-emerald-600 font-mono truncate max-w-md">{proposalLink}</p>
                </div>
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  onClick={() => { navigator.clipboard.writeText(proposalLink); toast.success('Copied!'); }}>
                  Copy Link
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Send Proposal Dialog */}
      <Dialog open={sendProposalOpen} onOpenChange={setSendProposalOpen}>
        <DialogContent className="max-w-md" data-testid="send-proposal-dialog">
          <DialogHeader>
            <DialogTitle>Send Proposal to Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-sky-50 rounded-xl text-center">
              <p className="text-sm text-sky-600">Proposal Total</p>
              <p className="text-3xl font-extrabold text-sky-800 font-mono">${(totals.total_price || 0).toLocaleString()}</p>
              <p className="text-xs text-sky-500">{bid?.inputs?.customer_name}</p>
            </div>
            <div>
              <Label>Customer Email (optional — for email delivery)</Label>
              <Input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)}
                placeholder="customer@email.com" className="h-11" data-testid="proposal-email-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Leave blank to just generate a shareable link</p>
            </div>
            <div className="p-3 bg-muted rounded-lg text-xs text-muted-foreground space-y-1">
              <p>A secure portal link will be generated (expires in 14 days)</p>
              <p>Customer can view scope, download PDF, and accept or decline</p>
              <p>You'll be notified when they view or respond</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendProposalOpen(false)}>Cancel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={handleSendProposal} data-testid="confirm-send-proposal-btn">
              Generate & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Assistant Panel */}
      {aiOpen && (
        <div className="fixed right-0 top-14 bottom-0 w-[380px] bg-white border-l border-slate-200 flex flex-col z-20 animate-slide-in" data-testid="ai-assistant-panel">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-sky-500 flex items-center justify-center"><Bot className="w-4 h-4 text-white" /></div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">AI Assistant</h3>
                <p className="text-[10px] text-slate-400">Powered by GPT-4o</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setAiOpen(false)} data-testid="close-ai-btn"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {chatHistory.length === 0 && (
                <div className="text-center py-8">
                  <Bot className="w-10 h-10 text-sky-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500 mb-2">Ask me about this bid!</p>
                  <div className="space-y-1.5">
                    {['What items am I missing?', 'Suggest equipment for this pool', 'Review my pricing and margins'].map(q => (
                      <button key={q} className="block w-full text-xs text-left px-3 py-2 bg-sky-50 rounded-lg text-sky-700 hover:bg-sky-100 transition-colors"
                        onClick={() => { setChatInput(q); }} data-testid={`ai-quick-${q.slice(0, 10)}`}>{q}</button>
                    ))}
                  </div>
                </div>
              )}
              {chatHistory.map((msg, i) => (
                <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                  {msg.role !== 'user' && <div className="w-7 h-7 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0"><Bot className="w-3.5 h-3.5 text-sky-600" /></div>}
                  <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${
                    msg.role === 'user' ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-700'
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  {msg.role === 'user' && <div className="w-7 h-7 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0"><User className="w-3.5 h-3.5 text-white" /></div>}
                </div>
              ))}
              {chatLoading && (
                <div className="flex gap-2">
                  <div className="w-7 h-7 rounded-full bg-sky-100 flex items-center justify-center"><Bot className="w-3.5 h-3.5 text-sky-600" /></div>
                  <div className="bg-slate-100 rounded-xl px-4 py-3">
                    <div className="flex gap-1"><div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" /><div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{animationDelay:'0.15s'}} /><div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{animationDelay:'0.3s'}} /></div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </ScrollArea>
          <div className="p-3 border-t border-slate-100">
            <div className="flex gap-2">
              <Input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSendChat()}
                placeholder="Ask about this bid..."
                className="flex-1"
                data-testid="ai-chat-input"
              />
              <Button size="icon" className="bg-sky-500 hover:bg-sky-600 text-white" onClick={handleSendChat} disabled={chatLoading}
                data-testid="ai-send-btn"><Send className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Line Item Dialog */}
      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent className="max-w-md" data-testid="add-line-item-dialog">
          <DialogHeader><DialogTitle>{editingItem ? 'Edit Line Item' : 'Add Custom Line Item'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={newItem.category} onValueChange={v => setNewItem(p => ({...p, category: v}))}>
                  <SelectTrigger data-testid="item-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={newItem.unit} onValueChange={v => setNewItem(p => ({...p, unit: v}))}>
                  <SelectTrigger data-testid="item-form-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{['EA','LF','SF','CY','HR','LOT'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Name</Label><Input value={newItem.name} onChange={e => setNewItem(p => ({...p, name: e.target.value}))} data-testid="item-form-name" /></div>
            <div className="grid grid-cols-4 gap-3">
              <div><Label>Qty</Label><Input type="number" value={newItem.qty} onChange={e => setNewItem(p => ({...p, qty: e.target.value}))} data-testid="item-form-qty" /></div>
              <div><Label>Cost ($)</Label><Input type="number" step="0.01" value={newItem.unit_cost} onChange={e => { const cost = parseFloat(e.target.value) || 0; const mk = parseFloat(newItem.markup_pct) || 0; setNewItem(p => ({...p, unit_cost: e.target.value, sell_price: (cost * (1 + mk / 100)).toFixed(2)})); }} data-testid="item-form-cost" /></div>
              <div><Label>Markup %</Label><Input type="number" value={newItem.markup_pct} onChange={e => { const mk = parseFloat(e.target.value) || 0; const cost = parseFloat(newItem.unit_cost) || 0; setNewItem(p => ({...p, markup_pct: e.target.value, sell_price: (cost * (1 + mk / 100)).toFixed(2)})); }} data-testid="item-form-markup" /></div>
              <div><Label>Sell $</Label><Input type="number" step="0.01" value={newItem.sell_price} onChange={e => setNewItem(p => ({...p, sell_price: e.target.value}))} placeholder="Auto" data-testid="item-form-sell" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={newItem.notes} onChange={e => setNewItem(p => ({...p, notes: e.target.value}))} rows={2} data-testid="item-form-notes" /></div>
            <div>
              <Label className="text-xs font-semibold">Proposal Section</Label>
              <Select value={newItem.pricing_bucket || 'base'} onValueChange={v => setNewItem(p => ({...p, pricing_bucket: v}))}>
                <SelectTrigger className="mt-1" data-testid="item-form-bucket"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="base">Base Pool Build</SelectItem>
                  <SelectItem value="selected_addon">Selected Enhancement</SelectItem>
                  <SelectItem value="available_option">Available Option (not in total)</SelectItem>
                  <SelectItem value="hidden">Hidden from Proposal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Switch checked={newItem.is_allowance || false} onCheckedChange={v => setNewItem(p => ({...p, is_allowance: v}))} data-testid="item-form-allowance-toggle" />
              <div className="flex-1 min-w-0">
                <Label className="text-amber-800 font-semibold text-xs">Allowance Item</Label>
                <p className="text-[10px] text-amber-700">Budget cap for selections. Upgrades beyond = change order.</p>
              </div>
            </div>
            {newItem.is_allowance && (
              <div>
                <Label>Allowance Amount ($)</Label>
                <Input type="number" step="100" value={newItem.allowance_amount || ''} onChange={e => setNewItem(p => ({...p, allowance_amount: e.target.value}))}
                  placeholder="Same as sell price" data-testid="item-form-allowance-amount" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddItemOpen(false)}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" onClick={handleAddItem} data-testid="item-form-save-btn">
              {editingItem ? 'Update' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bid Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="bid-preview-dialog">
          <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-bold text-slate-800">Customer Proposal Preview</h3>
              <span className="text-[10px] text-slate-400 font-mono" data-testid="preview-timestamp">Updated: {bid?.updated_at ? new Date(bid.updated_at).toLocaleString() : 'just now'}</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleDownloadProposal} data-testid="preview-download-html"><Download className="w-3.5 h-3.5 mr-1" /> HTML</Button>
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => {
                // Open proposal HTML in new window and trigger print (Save as PDF)
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                  printWindow.document.write(previewHtml);
                  printWindow.document.close();
                  setTimeout(() => { printWindow.print(); }, 500);
                } else {
                  toast.error('Popup blocked — allow popups to print PDF');
                }
              }} data-testid="preview-download-pdf"><Download className="w-3.5 h-3.5 mr-1" /> Save as PDF</Button>
            </div>
          </div>
          <div className="p-6">
            <iframe
              srcDoc={previewHtml}
              className="w-full border border-slate-200 rounded-lg"
              style={{ minHeight: '2400px' }}
              title="Proposal Preview"
              sandbox=""
            />
          </div>
        </DialogContent>
      </Dialog>
      {/* Pre-Send Checklist Dialog */}
      <Dialog open={checklistOpen} onOpenChange={setChecklistOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="presend-checklist-dialog">
          <DialogHeader>
            <DialogTitle>Pre-Send Checklist</DialogTitle>
            <p className="text-xs font-medium text-[#6b8a9e]">Confirm each item before sending this bid to the customer.</p>
          </DialogHeader>
          {checklistData && (
            <div className="space-y-2">
              {[
                { key: 'equipment', label: 'Equipment package is correct',
                  detail: `Pentair ${checklistData.equipment_package || 'Standard'} package${checklistData.has_equipment_items ? '' : ' — NO EQUIPMENT ITEMS IN BID'}` },
                { key: 'utility_scope', label: 'Utility run scope is accurate',
                  detail: `Electric: ${checklistData.utility_distance_electric}ft (${checklistData.utility_included_electric}ft included). Gas: ${checklistData.utility_distance_gas}ft (${checklistData.utility_included_gas}ft included)` },
                { key: 'extra_footage', label: 'Extra utility footage pricing is correct',
                  detail: checklistData.extra_electric_ft > 0 || checklistData.extra_gas_ft > 0 ?
                    `Extra electric: ${checklistData.extra_electric_ft}ft. Extra gas: ${checklistData.extra_gas_ft}ft.` :
                    'No extra footage — all within included base.' },
                { key: 'rock_dig', label: 'Rock / hard dig assumption is documented',
                  detail: checklistData.rock_hard_dig ? 'Rock/hard dig contingency IS included' : 'Standard soil assumed — rock dig NOT included. Rock clause applies.' },
                { key: 'decking', label: 'Decking scope is clear',
                  detail: checklistData.deck_included ? `${checklistData.deck_sqft} SF of ${checklistData.deck_material || 'decking'} included` : 'Decking is NOT included in this bid' },
                { key: 'exclusions', label: 'Exclusions and assumptions reviewed',
                  detail: 'Standard HDH exclusions apply. Custom exclusions set in Proposal Details.' },
                { key: 'turnkey', label: 'Turnkey vs owner responsibility is clear',
                  detail: [
                    !checklistData.panel_upgrade ? 'Panel upgrade: homeowner responsibility' : null,
                    !checklistData.gas_meter_upgrade ? 'Gas meter: homeowner responsibility' : null,
                    !checklistData.deck_included ? 'Decking: not included' : null,
                  ].filter(Boolean).join('. ') || 'Fully turnkey — all items included.' },
                { key: 'price', label: `Total price confirmed: $${(checklistData.total_price || 0).toLocaleString()}`, detail: '' },
              ].map(item => (
                <div key={item.key}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    checklistConfirmed[item.key] ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-[#d4e6ef] hover:bg-[#f5f9fb]'
                  }`}
                  onClick={() => setChecklistConfirmed(p => ({ ...p, [item.key]: !p[item.key] }))}
                  data-testid={`checklist-${item.key}`}
                >
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    checklistConfirmed[item.key] ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
                  }`}>
                    {checklistConfirmed[item.key] && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-bold ${checklistConfirmed[item.key] ? 'text-emerald-800' : 'text-[#1e3a50]'}`}>{item.label}</p>
                    {item.detail && <p className="text-[10px] font-medium text-[#6b8a9e] mt-0.5">{item.detail}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="flex-col gap-2">
            <p className={`text-xs font-bold text-center w-full ${Object.keys(checklistConfirmed).length >= 8 ? 'text-emerald-600' : 'text-amber-600'}`}>
              {Object.keys(checklistConfirmed).filter(k => checklistConfirmed[k]).length}/8 confirmed
            </p>
            <div className="flex gap-2 w-full justify-end">
              <Button variant="outline" onClick={() => setChecklistOpen(false)}>Cancel</Button>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={Object.keys(checklistConfirmed).filter(k => checklistConfirmed[k]).length < 8}
                onClick={handleChecklistSend}
                data-testid="checklist-send-btn">
                All Confirmed — Send Bid
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Homeowner Dialog */}
      <Dialog open={inviteHomeownerOpen} onOpenChange={setInviteHomeownerOpen}>
        <DialogContent className="max-w-md" data-testid="invite-homeowner-dialog">
          <DialogHeader>
            <DialogTitle>Create Homeowner Portal Login</DialogTitle>
            <p className="text-xs font-medium text-[#6b8a9e]">Give your customer access to view their project progress, photos, and documents.</p>
          </DialogHeader>
          {hoCreated ? (
            <div className="space-y-3">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <p className="text-sm font-bold text-emerald-800 mb-3">Account Created!</p>
                <div className="space-y-2 text-xs">
                  <div><span className="font-bold text-emerald-700">Name:</span> <span className="font-mono">{hoCreated.name}</span></div>
                  <div><span className="font-bold text-emerald-700">Email:</span> <span className="font-mono">{hoCreated.email}</span></div>
                  <div><span className="font-bold text-emerald-700">Password:</span> <span className="font-mono select-all bg-white px-2 py-0.5 rounded">{hoCreated.password}</span></div>
                  <div><span className="font-bold text-emerald-700">Portal:</span> <span className="font-mono">{window.location.origin}/portal/login</span></div>
                </div>
              </div>
              <Button className="w-full bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={() => {
                navigator.clipboard.writeText(`Your HDH Pools & Spas project portal:\nURL: ${window.location.origin}/portal/login\nEmail: ${hoCreated.email}\nPassword: ${hoCreated.password}`);
                toast.success('Login details copied!');
              }}>Copy Login Details to Send to Customer</Button>
              <Button variant="outline" className="w-full" onClick={() => { setHoCreated(null); setInviteHomeownerOpen(false); }}>Done</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div><Label className="text-xs font-bold">Customer Name *</Label><Input value={hoForm.name} onChange={e => setHoForm(p => ({...p, name: e.target.value}))} placeholder={bid?.inputs?.customer_name || 'John & Sarah Miller'} data-testid="ho-invite-name" /></div>
              <div><Label className="text-xs font-bold">Customer Email *</Label><Input type="email" value={hoForm.email} onChange={e => setHoForm(p => ({...p, email: e.target.value}))} placeholder="customer@email.com" data-testid="ho-invite-email" /></div>
              <div><Label className="text-xs font-bold">Phone</Label><Input value={hoForm.phone} onChange={e => setHoForm(p => ({...p, phone: e.target.value}))} /></div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteHomeownerOpen(false)}>Cancel</Button>
                <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" disabled={!hoForm.name || !hoForm.email}
                  onClick={async () => {
                    try {
                      const r = await api.post('/homeowner/admin/create-account', { ...hoForm, projects: [] });
                      setHoCreated({ name: r.data.name, email: r.data.email, password: r.data.temp_password });
                      toast.success('Homeowner account created');
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }} data-testid="ho-invite-create">Create Account</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DiscountControls({ bid, id, totals, loadBid }) {
  const [localValue, setLocalValue] = useState(bid.discount_value || '');
  const [localLabel, setLocalLabel] = useState(bid.discount_label || '');

  // Sync from parent when bid changes (e.g. after type change)
  useEffect(() => {
    setLocalValue(bid.discount_value || '');
    setLocalLabel(bid.discount_label || '');
  }, [bid.discount_type, bid.discount_value, bid.discount_label]);

  const saveValue = async () => {
    const val = parseFloat(localValue) || 0;
    if (val !== (bid.discount_value || 0)) {
      await api.put(`/bids/${id}`, { discount_value: val });
      loadBid();
    }
  };

  const saveLabel = async () => {
    if (localLabel !== (bid.discount_label || '')) {
      await api.put(`/bids/${id}`, { discount_label: localLabel });
      loadBid();
    }
  };

  return (
    <div className="mb-4 p-3 bg-amber-50/50 rounded-lg border border-amber-200/50">
      <p className="text-[10px] font-bold text-amber-700 uppercase mb-2">Proposal Discount</p>
      <div className="flex items-center gap-2">
        <select
          value={bid.discount_type || ''}
          onChange={async (e) => {
            const val = e.target.value || null;
            await api.put(`/bids/${id}`, { discount_type: val, discount_value: val ? (parseFloat(localValue) || 0) : 0 });
            loadBid();
          }}
          className="text-xs h-8 px-2 rounded border border-amber-200 bg-white"
          data-testid="discount-type"
        >
          <option value="">No Discount</option>
          <option value="percent">% Percent</option>
          <option value="amount">$ Amount</option>
        </select>
        {bid.discount_type && (
          <>
            <Input
              type="number"
              step={bid.discount_type === 'percent' ? '0.5' : '100'}
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onBlur={saveValue}
              onKeyDown={(e) => { if (e.key === 'Enter') saveValue(); }}
              className="w-24 h-8 text-xs font-mono"
              placeholder={bid.discount_type === 'percent' ? '5' : '2000'}
              data-testid="discount-value"
            />
            <Input
              value={localLabel}
              onChange={(e) => setLocalLabel(e.target.value)}
              onBlur={saveLabel}
              onKeyDown={(e) => { if (e.key === 'Enter') saveLabel(); }}
              className="flex-1 h-8 text-xs"
              placeholder="Label (e.g. Summer Sale)"
              data-testid="discount-label"
            />
          </>
        )}
        {bid.discount_type && totals.discount_amount > 0 && (
          <span className="text-xs font-bold text-red-600 font-mono whitespace-nowrap">-${Math.round(totals.discount_amount).toLocaleString()}</span>
        )}
      </div>
    </div>
  );
}





// ─── Design Package Panel ───

function DesignPackagePanel({ bid, bidId, loadBid }) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState('');
  const [localNotes, setLocalNotes] = useState(bid?.design_notes || '');

  useEffect(() => { setLocalNotes(bid?.design_notes || ''); }, [bid?.design_notes]);

  const slots = [
    { key: 'concept_plan', label: 'Concept Plan', size: 'secondary' },
    { key: 'daytime_render', label: 'Daytime Hero', size: 'primary' },
    { key: 'evening_render', label: 'Twilight Luxury', size: 'secondary' },
  ];

  const load = () => api.get(`/bids/${bidId}/design-images`).then(r => setImages(r.data)).catch(() => {});
  useEffect(() => { if (bidId) load(); }, [bidId]);

  const compressImage = (file, maxWidth, quality) => new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w > maxWidth) { h = Math.round(h * (maxWidth / w)); w = maxWidth; }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Compression failed')), 'image/jpeg', quality);
    };
    img.onerror = () => reject(new Error('Could not read image'));
    img.src = URL.createObjectURL(file);
  });

  const handleUpload = async (slot, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error('File too large. Max 15MB.'); return; }
    setUploading(slot);
    try {
      const processed = await compressImage(file, 2000, 0.85);
      const fd = new FormData();
      fd.append('file', processed, file.name || 'image.jpg');
      await api.post(`/bids/${bidId}/design-images/${slot}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Image uploaded');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    setUploading('');
  };

  const handleRemove = async (slot) => { await api.delete(`/bids/${bidId}/design-images/${slot}`); toast.success('Removed'); load(); };
  const getImage = (slot) => images.find(i => i.slot === slot);

  const saveNotes = async () => {
    if (localNotes !== (bid?.design_notes || '')) {
      await api.put(`/bids/${bidId}`, { design_notes: localNotes }); loadBid();
    }
  };

  // Specs strip
  const inp = bid?.inputs || {};
  const shapeLabels = { rectangle:'Rectangle', auto_cover_rectangle:'Auto-Cover Rect', roman:'Roman', freeform:'Freeform', grecian:'Grecian', l_shape:'L-Shape', lazy_l:'Lazy-L', kidney:'Kidney', geometric_custom:'Custom', compact_plunge:'Plunge', negative_edge:'Neg. Edge' };
  const specLine1 = [shapeLabels[inp.pool_shape_template] || '', inp.length && inp.width ? `${inp.length}'×${inp.width}'` : '', inp.spa_type && inp.spa_type !== 'none' ? `${inp.spa_type} spa` : ''].filter(Boolean).join(' · ');
  const styleLabels = { modern:'Modern', contemporary:'Contemporary', traditional:'Traditional', mediterranean:'Mediterranean', desert_modern:'Desert Modern', transitional:'Transitional' };
  const specLine2 = [styleLabels[inp.home_style] || '', inp.deck_material_style?.replace('_', ' ') || '', inp.lighting_mood?.replace('_', ' ') || ''].filter(Boolean).join(' · ');
  const featureTags = [];
  if (inp.water_feature_type && inp.water_feature_type !== 'none') featureTags.push(inp.water_feature_type.replace('_', ' '));
  if (inp.tanning_ledge_size && inp.tanning_ledge_size !== 'none') featureTags.push('Tanning Ledge');
  if (inp.slide_placement && inp.slide_placement !== 'none') featureTags.push('Slide');
  const specLine3 = featureTags.slice(0, 3).join(' · ');

  // Image set status
  const setStatus = bid?.render_image_set_status || 'draft';
  const version = bid?.render_version || 1;
  const statusColors = { draft: 'bg-slate-100 text-slate-600', active: 'bg-sky-100 text-sky-700', approved: 'bg-emerald-100 text-emerald-700' };

  const heroImg = getImage('daytime_render');
  const conceptImg = getImage('concept_plan');
  const twilightImg = getImage('evening_render');

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-6" data-testid="design-package-panel">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-[#F59E0B]" />
            <p className="text-sm font-bold text-[#1e3a50]">Design Package</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-slate-400">v{version}</span>
            <Badge className={`text-[9px] ${statusColors[setStatus]}`}>{setStatus}</Badge>
            <div className="flex gap-0.5 ml-2">
              {['draft', 'active', 'approved'].map(s => (
                <button key={s} onClick={async () => { await api.put(`/bids/${bidId}`, { render_image_set_status: s }); loadBid(); }}
                  className={`text-[8px] px-1.5 py-0.5 rounded ${setStatus === s ? 'bg-[#0F1D32] text-white' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}>{s}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Image layout: Hero large left, 2 smaller right */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {/* Hero / Daytime - large */}
          <div className="col-span-2 row-span-2">
            {heroImg ? (
              <div className="relative group h-full">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/bids/${bidId}/design-images/daytime_render/data`} alt="Daytime Hero" className="w-full h-full object-cover rounded-lg border border-slate-200 min-h-[200px]" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                  <label className="px-2 py-1 bg-white text-[10px] font-semibold rounded cursor-pointer">Replace<input type="file" accept="image/*" className="hidden" onChange={e => handleUpload('daytime_render', e.target.files[0])} /></label>
                  <button onClick={() => handleRemove('daytime_render')} className="px-2 py-1 bg-red-500 text-white text-[10px] font-semibold rounded">Remove</button>
                </div>
                <span className="absolute bottom-2 left-2 text-[9px] bg-black/60 text-white px-1.5 py-0.5 rounded">{heroImg.caption || 'Daytime Hero'}</span>
              </div>
            ) : (
              <label className={`flex flex-col items-center justify-center w-full h-full min-h-[200px] border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-[#F59E0B] transition-colors ${uploading === 'daytime_render' ? 'opacity-50' : ''}`}>
                <Plus className="w-6 h-6 text-slate-300 mb-1" /><span className="text-xs text-slate-400">Daytime Hero</span>
                <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload('daytime_render', e.target.files[0])} />
              </label>
            )}
          </div>
          {/* Concept Plan - small */}
          <div>
            {conceptImg ? (
              <div className="relative group">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/bids/${bidId}/design-images/concept_plan/data`} alt="Concept Plan" className="w-full aspect-[4/3] object-cover rounded-lg border border-slate-200" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                  <label className="px-1.5 py-0.5 bg-white text-[9px] font-semibold rounded cursor-pointer">Replace<input type="file" accept="image/*" className="hidden" onChange={e => handleUpload('concept_plan', e.target.files[0])} /></label>
                  <button onClick={() => handleRemove('concept_plan')} className="px-1.5 py-0.5 bg-red-500 text-white text-[9px] rounded">X</button>
                </div>
                <span className="absolute bottom-1 left-1 text-[8px] bg-black/60 text-white px-1 py-0.5 rounded">{conceptImg.caption || 'Concept Plan'}</span>
              </div>
            ) : (
              <label className={`flex flex-col items-center justify-center w-full aspect-[4/3] border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-[#F59E0B] ${uploading === 'concept_plan' ? 'opacity-50' : ''}`}>
                <Plus className="w-4 h-4 text-slate-300" /><span className="text-[9px] text-slate-400">Concept</span>
                <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload('concept_plan', e.target.files[0])} />
              </label>
            )}
          </div>
          {/* Twilight - small */}
          <div>
            {twilightImg ? (
              <div className="relative group">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/bids/${bidId}/design-images/evening_render/data`} alt="Twilight" className="w-full aspect-[4/3] object-cover rounded-lg border border-slate-200" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                  <label className="px-1.5 py-0.5 bg-white text-[9px] font-semibold rounded cursor-pointer">Replace<input type="file" accept="image/*" className="hidden" onChange={e => handleUpload('evening_render', e.target.files[0])} /></label>
                  <button onClick={() => handleRemove('evening_render')} className="px-1.5 py-0.5 bg-red-500 text-white text-[9px] rounded">X</button>
                </div>
                <span className="absolute bottom-1 left-1 text-[8px] bg-black/60 text-white px-1 py-0.5 rounded">{twilightImg.caption || 'Twilight'}</span>
              </div>
            ) : (
              <label className={`flex flex-col items-center justify-center w-full aspect-[4/3] border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-[#F59E0B] ${uploading === 'evening_render' ? 'opacity-50' : ''}`}>
                <Plus className="w-4 h-4 text-slate-300" /><span className="text-[9px] text-slate-400">Twilight</span>
                <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload('evening_render', e.target.files[0])} />
              </label>
            )}
          </div>
        </div>

        {/* Specs strip */}
        <div className="bg-[#0F1D32] rounded-lg px-4 py-2.5 mb-3">
          {specLine1 && <p className="text-[11px] text-white font-medium">{specLine1}</p>}
          {specLine2 && <p className="text-[10px] text-white/50">{specLine2}</p>}
          {specLine3 && <p className="text-[10px] text-[#F59E0B]">{specLine3}</p>}
        </div>

        {/* Design Notes */}
        <Textarea value={localNotes} onChange={e => setLocalNotes(e.target.value)} onBlur={saveNotes}
          rows={2} className="text-xs" placeholder="Design notes (shown below images on proposal)..." data-testid="design-notes-textarea" />
      </CardContent>
    </Card>
  );
}

// ─── Materials & Style Inputs ───

function MaterialsStyleInputs({ bid, bidId, loadBid }) {
  const [expanded, setExpanded] = useState(false);
  const inp = bid?.inputs || {};
  const u = async (key, val) => {
    const newInputs = { ...bid.inputs, [key]: val };
    await api.put(`/bids/${bidId}`, { inputs: newInputs });
    loadBid();
  };

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-6" data-testid="materials-style-inputs">
      <CardContent className="p-4">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-[#F59E0B]" />
            <p className="text-sm font-bold text-[#1e3a50]">Materials & Style</p>
            <span className="text-[10px] text-slate-400">Drives render prompts</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
        {expanded && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label className="text-[10px]">Home Style</Label>
              <select value={inp.home_style || ''} onChange={e => u('home_style', e.target.value)} className="w-full h-8 text-xs border rounded px-2">
                <option value="">Select...</option><option value="modern">Modern</option><option value="contemporary">Contemporary</option><option value="traditional">Traditional</option><option value="mediterranean">Mediterranean</option><option value="desert_modern">Desert Modern</option><option value="transitional">Transitional</option>
              </select></div>
            <div><Label className="text-[10px]">Landscape Style</Label>
              <select value={inp.landscape_style || ''} onChange={e => u('landscape_style', e.target.value)} className="w-full h-8 text-xs border rounded px-2">
                <option value="">Select...</option><option value="clean_modern">Clean Modern</option><option value="resort">Resort</option><option value="tropical">Tropical</option><option value="desert">Desert</option><option value="lush_residential">Lush Residential</option>
              </select></div>
            <div><Label className="text-[10px]">Deck Material</Label>
              <select value={inp.deck_material_style || ''} onChange={e => u('deck_material_style', e.target.value)} className="w-full h-8 text-xs border rounded px-2">
                <option value="">Select...</option><option value="light_pavers">Light Pavers</option><option value="travertine">Travertine</option><option value="concrete">Concrete</option><option value="stamped_concrete">Stamped Concrete</option><option value="porcelain">Porcelain</option><option value="custom">Custom</option>
              </select></div>
            <div><Label className="text-[10px]">Coping Style</Label>
              <select value={inp.coping_style || ''} onChange={e => u('coping_style', e.target.value)} className="w-full h-8 text-xs border rounded px-2">
                <option value="">Select...</option><option value="straight_modern">Straight Modern</option><option value="bullnose">Bullnose</option><option value="cantilever">Cantilever</option><option value="stone_coping">Stone Coping</option><option value="custom">Custom</option>
              </select></div>
            <div><Label className="text-[10px]">Water Color / Tone</Label>
              <select value={inp.water_color_tone || ''} onChange={e => u('water_color_tone', e.target.value)} className="w-full h-8 text-xs border rounded px-2">
                <option value="">Select...</option><option value="light_caribbean">Light Caribbean Blue</option><option value="deep_blue">Deep Blue</option><option value="dark_reflective">Dark Reflective</option><option value="aqua">Aqua</option><option value="custom">Custom</option>
              </select></div>
            <div><Label className="text-[10px]">Lighting Mood</Label>
              <select value={inp.lighting_mood || ''} onChange={e => u('lighting_mood', e.target.value)} className="w-full h-8 text-xs border rounded px-2">
                <option value="">Select...</option><option value="bright_daylight">Bright Daylight</option><option value="warm_sunset">Warm Sunset</option><option value="twilight_glow">Twilight Glow</option><option value="evening_luxury">Evening Luxury</option>
              </select></div>
            <div className="col-span-2 sm:col-span-3">
              <Label className="text-[10px]">Render Preset (bid-wide style direction)</Label>
              <select value={bid?.render_preset || ''} onChange={async e => { await api.put(`/bids/${bidId}`, { render_preset: e.target.value }); loadBid(); }} className="w-full h-8 text-xs border rounded px-2" data-testid="render-preset">
                <option value="">Default</option><option value="daytime_hero">Daytime Hero</option><option value="twilight_luxury">Twilight Luxury</option><option value="feature_focus">Feature Focus</option><option value="presentation_sheet">Presentation Sheet</option>
              </select>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Prompt Assembly Engine (Upgraded) ───

const SHAPE_LANG = {
  rectangle: 'rectangular gunite swimming pool', auto_cover_rectangle: 'rectangular gunite pool with integrated automatic cover track',
  roman: 'Roman-end gunite pool with semicircular ends', freeform: 'freeform gunite pool with flowing organic curves',
  grecian: 'Grecian gunite pool with angular clipped corners', l_shape: 'L-shaped gunite pool',
  lazy_l: 'Lazy-L gunite pool with offset wing', kidney: 'kidney-shaped gunite pool with classic curved form',
  geometric_custom: 'custom geometric gunite pool with mixed angles', compact_plunge: 'compact plunge pool with clean proportions',
  negative_edge: 'negative-edge vanishing-edge infinity pool',
};
const ENTRY_LANG = { corner_steps: 'corner entry steps with handrail', side_steps: 'side-mounted entry steps', full_width_steps: 'full-width beach-style entry steps across the shallow end', recessed_steps: 'recessed swim-out entry steps', no_steps: '' };
const DEPTH_LANG = { flat: 'uniform flat-bottom depth', sport_bottom: 'gradual sport-bottom slope from shallow to deep', deep_end_hopper: 'deep-end hopper with steep transition' };
const LEDGE_LANG = { small: 'small sun shelf at the shallow end', medium: 'generous tanning ledge along the shallow end with lounge space', large: 'expansive tanning ledge spanning the full shallow end' };
const SLIDE_LANG = { left: 'pool slide integrated on the left side', right: 'pool slide integrated on the right side', deep_end: 'pool slide positioned at the deep end', custom: 'integrated pool slide' };
const WATER_LANG = { sheer_descents: 'sheer descent water features with illuminated sheets of water', bubblers: 'raised bubbler water features on the tanning ledge', deck_jets: 'arcing deck jet water features', scuppers: 'wall-mounted scupper water features', rock_waterfall: 'natural stacked-stone rock waterfall with grotto-style cascade', multiple: 'combination water features including spillways and jets' };
const FIRE_LANG = { bowls: 'raised fire bowl accents on pedestals flanking the pool', fire_pit: 'integrated fire pit feature near the pool area', both: 'fire bowl accents and a dedicated fire pit integrated into the hardscape' };
const BENCH_LANG = { one_side: 'built-in bench seating along one wall', deep_end: 'submerged bench seating at the deep end', wraparound: 'wraparound perimeter bench seating' };
const HOME_LANG = { modern: 'modern home with clean geometric lines and large glass panels', contemporary: 'contemporary residence with mixed materials', traditional: 'traditional home with warm earth tones', mediterranean: 'Mediterranean-style residence with stucco and tile roof', desert_modern: 'desert modern home with flat roof, clean stucco, and warm neutrals', transitional: 'transitional-style home blending classic and modern elements' };
const LANDSCAPE_LANG = { clean_modern: 'clean modern landscaping with geometric planters and ornamental grasses', resort: 'resort-inspired landscaping with mature palms, boulders, and lush beds', tropical: 'lush tropical plantings with palms, birds of paradise, and dense canopy', desert: 'desert-native landscaping with agave, succulents, and decomposed granite', lush_residential: 'established lush residential landscaping with mature trees and manicured beds' };
const DECK_LANG = { light_pavers: 'light-colored paver decking in a running bond pattern', travertine: 'natural travertine stone decking with warm ivory tones', concrete: 'brushed light-gray concrete decking', stamped_concrete: 'stamped concrete decking with natural stone pattern', porcelain: 'large-format porcelain tile decking' };
const COPING_LANG = { straight_modern: 'straight-cut modern coping with clean square edges', bullnose: 'tumbled bullnose coping with rounded edges', cantilever: 'cantilevered poured concrete coping', stone_coping: 'natural stone coping with organic texture' };
const WATER_COLOR_LANG = { light_caribbean: 'light Caribbean blue pool water with brilliant clarity', deep_blue: 'deep sapphire blue pool water', dark_reflective: 'dark reflective pool water with mirror-like surface', aqua: 'vibrant bright aqua pool water' };
const LIGHTING_LANG = { bright_daylight: 'bright clear daylight with crisp natural shadows and blue sky', warm_sunset: 'warm golden-hour sunset lighting casting long soft shadows', twilight_glow: 'deep twilight sky transitioning from blue to purple, warm ambient glow from landscape fixtures', evening_luxury: 'dramatic evening lighting with high-contrast pool glow and accent uplighting' };

function assemblePromptParts(i) {
  const parts = {};
  const shapeName = SHAPE_LANG[i.pool_shape_template] || (i.pool_type === 'gunite' ? 'custom gunite swimming pool' : 'swimming pool');
  const dims = (i.length && i.width) ? `approximately ${i.length} feet by ${i.width} feet` : '';
  parts.geometry = dims ? `${shapeName}, ${dims}` : shapeName;
  if (i.shallow_depth && i.deep_depth) parts.depth = `depth transitioning from ${i.shallow_depth} feet at the shallow end to ${i.deep_depth} feet at the deep end, ${DEPTH_LANG[i.depth_profile_type] || 'gradual slope'}`;
  else if (i.avg_depth) parts.depth = `average depth of ${i.avg_depth} feet`;
  if (i.spa_type && i.spa_type !== 'none') {
    let spa = i.spa_type === 'attached' ? 'attached' : 'separate detached';
    if (i.spa_shape) spa += ` ${i.spa_shape}`;
    spa += ' spa';
    if (i.spa_location && i.spa_location !== 'custom') spa += ` positioned on the ${i.spa_location} side of the pool`;
    if (i.spa_spillway && i.spa_spillway !== 'none') spa += i.spa_spillway === 'spillover' ? ' with a graceful spillover edge flowing into the main pool' : ' with a raised wall spillway';
    parts.spa = spa;
  }
  if (i.entry_type && i.entry_type !== 'no_steps') parts.entry = ENTRY_LANG[i.entry_type] || '';
  if (i.tanning_ledge_size && i.tanning_ledge_size !== 'none') parts.ledge = LEDGE_LANG[i.tanning_ledge_size] || '';
  if (i.bench_seating && i.bench_seating !== 'none') parts.bench = BENCH_LANG[i.bench_seating] || '';
  if (i.slide_placement && i.slide_placement !== 'none') parts.slide = SLIDE_LANG[i.slide_placement] || '';
  if (i.water_feature_type && i.water_feature_type !== 'none') parts.water = WATER_LANG[i.water_feature_type] || '';
  if (i.fire_feature_type && i.fire_feature_type !== 'none') parts.fire = FIRE_LANG[i.fire_feature_type] || '';
  if (i.home_style) parts.home = HOME_LANG[i.home_style] || '';
  if (i.landscape_style) parts.landscape = LANDSCAPE_LANG[i.landscape_style] || '';
  if (i.deck_material_style) parts.decking = DECK_LANG[i.deck_material_style] || i.deck_material_style.replace(/_/g, ' ');
  if (i.coping_style) parts.coping = COPING_LANG[i.coping_style] || '';
  if (i.water_color_tone) parts.waterColor = WATER_COLOR_LANG[i.water_color_tone] || '';
  if (i.lighting_mood) parts.lightMood = LIGHTING_LANG[i.lighting_mood] || '';
  if (i.pool_design_notes?.trim()) parts.notes = i.pool_design_notes.trim();
  const site = [];
  if (i.pool_orientation && i.pool_orientation !== 'custom') site.push(`pool oriented ${i.pool_orientation} to the rear of the home`);
  if (i.yard_condition === 'mild_slope') site.push('gently sloped backyard terrain');
  if (i.yard_condition === 'significant_slope') site.push('significantly sloped lot with retaining elements');
  if (i.view_condition === 'view_lot') site.push('elevated view lot overlooking a scenic landscape');
  if (i.view_condition === 'tight_courtyard') site.push('tight courtyard-style backyard with close boundary walls');
  if (site.length) parts.site = site.join(', ');
  return parts;
}

function buildDaytimePrompt(parts) {
  const lines = ['Photorealistic luxury residential pool visualization. Elevated 3/4 backyard perspective from approximately second-story height, capturing the full pool, deck, and immediate landscape.'];
  const desc = [parts.geometry, parts.depth, parts.spa, parts.entry, parts.ledge, parts.bench, parts.slide, parts.water, parts.fire].filter(Boolean);
  if (desc.length) lines.push(desc.join('. ') + '.');
  const mats = [parts.decking, parts.coping].filter(Boolean);
  if (mats.length) lines.push(`Materials: ${mats.join(', ')}.`);
  const context = [parts.home, parts.landscape].filter(Boolean);
  if (context.length) lines.push(`Context: ${context.join('. ')}.`);
  if (parts.site) lines.push(`Site: ${parts.site}.`);
  const light = parts.lightMood || 'bright clear daylight with crisp natural shadows and blue sky';
  const water = parts.waterColor || 'crystal clear turquoise pool water with visible bottom detail';
  lines.push(`Lighting: ${light}. Water appearance: ${water}.`);
  lines.push('Render style: photorealistic architectural visualization, professional presentation quality, sharp detail, accurate material textures, realistic reflections and water caustics.');
  if (parts.notes) lines.push(`Additional design direction: ${parts.notes}`);
  lines.push('Avoid: people, text, watermarks, brand logos, distorted pool geometry, unrealistic fantasy elements, oversaturated colors, floating objects.');
  return lines.join('\n');
}

function buildConceptPlanPrompt(parts) {
  const lines = ['Architectural pool concept plan rendered from a perfectly straight-down overhead perspective. Professional design presentation quality.'];
  const desc = [parts.geometry, parts.spa, parts.entry, parts.ledge, parts.bench, parts.slide, parts.water].filter(Boolean);
  if (desc.length) lines.push(`Pool layout: ${desc.join('. ')}.`);
  const mats = [parts.decking, parts.coping].filter(Boolean);
  if (mats.length) lines.push(`Surrounding surfaces: ${mats.join(', ')}.`);
  if (parts.site) lines.push(`Property context: ${parts.site}.`);
  lines.push('Visual style: clean architectural plan with subtle colored fill indicating water, deck, and landscaping zones. Precise geometry with sharp edges. Property boundary and house footprint visible for context. Neutral even lighting with no harsh shadows. Professional drafting presentation quality.');
  if (parts.notes) lines.push(`Additional design direction: ${parts.notes}`);
  lines.push('Avoid: any perspective angle or camera tilt, 3D shading effects, people, brand logos, heavy annotation text, artistic painterly interpretation, dramatic lighting.');
  return lines.join('\n');
}

function buildEveningPrompt(parts) {
  const lines = [];
  if (parts.spa) {
    lines.push('Photorealistic twilight luxury pool and spa scene. Low dramatic camera angle from deck level emphasizing the spa and its connection to the main pool.');
    lines.push([parts.geometry, parts.spa + ' illuminated with color-changing underwater LED lighting', parts.water, parts.fire, parts.ledge, parts.decking].filter(Boolean).join('. ') + '.');
  } else if (parts.fire) {
    lines.push('Photorealistic twilight pool scene with dramatic fire feature emphasis. Low angle capturing flame reflections on the water surface.');
    lines.push([parts.geometry, parts.fire + ' actively lit with dancing flames', parts.water, parts.ledge, parts.decking].filter(Boolean).join('. ') + '.');
  } else {
    lines.push('Photorealistic evening twilight pool visualization. Warm luxury residential atmosphere captured from a low perspective near the deck surface.');
    lines.push([parts.geometry, parts.water, parts.ledge, parts.slide, parts.decking].filter(Boolean).join('. ') + '.');
  }
  const context = [parts.home, parts.landscape].filter(Boolean);
  if (context.length) lines.push(`Context: ${context.join('. ')}.`);
  if (parts.site) lines.push(`Site: ${parts.site}.`);
  const mood = parts.lightMood || 'deep twilight sky transitioning from blue to purple, warm ambient glow from landscape fixtures';
  const water = parts.waterColor || 'luminous turquoise pool water glowing from underwater LED lights';
  lines.push(`Lighting: ${mood}. Deep blue-purple twilight sky above. Warm amber and white landscape path lighting, accent uplighting on trees and hardscape features. Water: ${water}.`);
  lines.push('Render style: photorealistic cinematic architectural visualization, luxury residential evening atmosphere, professional quality, accurate light interaction and reflections.');
  if (parts.notes) lines.push(`Additional design direction: ${parts.notes}`);
  lines.push('Avoid: people, text, watermarks, brand logos, over-saturated neon colors, distorted pool geometry, unrealistic fantasy glow effects.');
  return lines.join('\n');
}

function buildFeatureFocusPrompt(parts) {
  const lines = [];
  // Priority: spa > ledge > slide > water > fire
  const primary = parts.spa || parts.ledge || parts.slide || parts.water || parts.fire;
  if (primary) {
    lines.push(`Photorealistic close-up detail render highlighting: ${primary}. Tight composition at eye level showcasing this feature integrated naturally within the pool environment.`);
  } else {
    lines.push('Photorealistic close-up detail render of the pool\'s most visually distinctive design element. Tight eye-level composition with shallow depth of field.');
  }
  const context = [parts.geometry, parts.decking, parts.coping].filter(Boolean);
  if (context.length) lines.push(`Pool context: ${context.join('. ')}.`);
  if (parts.home) lines.push(`Home visible in background: ${parts.home}.`);
  const water = parts.waterColor || 'clear inviting pool water with visible light refraction';
  const light = parts.lightMood || 'natural directional lighting emphasizing texture and form';
  lines.push(`${water}. ${light}. Visible material textures, craftsmanship details, and natural wear patterns that convey quality construction.`);
  lines.push('Render style: photorealistic with shallow depth of field, luxury residential, professional architectural photography aesthetic, sharp focus on primary subject.');
  if (parts.notes) lines.push(`Additional design direction: ${parts.notes}`);
  lines.push('Avoid: people, text, watermarks, brand logos, wide-angle barrel distortion, cluttered background elements, unrealistic material sheen.');
  return lines.join('\n');
}

function RenderPromptBuilder({ bid, bidId, loadBid }) {
  const [expanded, setExpanded] = useState(false);
  const [prompts, setPrompts] = useState({ concept_plan: '', daytime_render: '', evening_render: '', feature_focus: '' });
  const [manual, setManual] = useState({ concept_plan: false, daytime_render: false, evening_render: false, feature_focus: false });

  useEffect(() => {
    if (bid) {
      if (bid.render_prompts) setPrompts(p => ({ ...p, ...bid.render_prompts }));
      if (bid.render_prompts_manual) setManual(m => ({ ...m, ...bid.render_prompts_manual }));
    }
  }, [bid?.render_prompts, bid?.render_prompts_manual]);

  const generateOne = (key) => {
    if (manual[key] && prompts[key]) {
      if (!window.confirm('Replace your edited prompt?\n\nRefreshing from pool data will overwrite your manual changes for this prompt.')) return;
    }
    const parts = assemblePromptParts(bid?.inputs || {});
    let text = '';
    if (key === 'concept_plan') text = buildConceptPlanPrompt(parts);
    else if (key === 'daytime_render') text = buildDaytimePrompt(parts);
    else if (key === 'evening_render') text = buildEveningPrompt(parts);
    else text = buildFeatureFocusPrompt(parts);
    setPrompts(p => ({ ...p, [key]: text }));
    setManual(m => ({ ...m, [key]: false }));
  };

  const generateAll = () => {
    const hasManual = Object.values(manual).some(Boolean) && Object.values(prompts).some(Boolean);
    if (hasManual && !window.confirm('Replace your edited prompts?\n\nRefreshing from pool data will overwrite all manual changes.')) return;
    const parts = assemblePromptParts(bid?.inputs || {});
    setPrompts({ concept_plan: buildConceptPlanPrompt(parts), daytime_render: buildDaytimePrompt(parts), evening_render: buildEveningPrompt(parts), feature_focus: buildFeatureFocusPrompt(parts) });
    setManual({ concept_plan: false, daytime_render: false, evening_render: false, feature_focus: false });
  };

  const saveAll = async () => {
    await api.put(`/bids/${bidId}`, { render_prompts: prompts, render_prompts_manual: manual });
    toast.success('Prompts saved');
    loadBid();
  };

  const copyPrompt = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  const PROMPT_SLOTS = [
    { key: 'daytime_render', label: 'Daytime Hero Render' },
    { key: 'evening_render', label: 'Twilight Luxury Render' },
    { key: 'concept_plan', label: 'Top-Down Concept Plan' },
    { key: 'feature_focus', label: 'Feature Focus Render' },
  ];

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-6" data-testid="render-prompt-builder">
      <CardContent className="p-4">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#F59E0B]" />
            <p className="text-sm font-bold text-[#1e3a50]">AI Render Prompt Assembly</p>
            <span className="text-[10px] text-slate-400">4 prompt types · auto from Pool Basics + Style</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
        {expanded && (
          <div className="mt-4 space-y-4">
            <div className="flex gap-2">
              <Button size="sm" className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0F1D32] font-bold" onClick={generateAll} data-testid="generate-all-btn">
                <Zap className="w-3.5 h-3.5 mr-1" /> Generate All from Pool Data
              </Button>
              <Button size="sm" variant="outline" onClick={saveAll} data-testid="save-prompts-btn">
                <Save className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
            </div>
            {PROMPT_SLOTS.map(slot => (
              <div key={slot.key}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs font-bold text-slate-600">{slot.label}</Label>
                    {manual[slot.key] && <span className="text-[9px] px-1.5 py-0.5 bg-violet-100 text-violet-600 rounded font-semibold">Edited</span>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] text-slate-500" onClick={() => generateOne(slot.key)} data-testid={`refresh-${slot.key}`}>
                      <Zap className="w-3 h-3 mr-0.5" /> Refresh
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] text-slate-500" onClick={() => copyPrompt(prompts[slot.key], slot.label)} data-testid={`copy-${slot.key}`}>
                      <Copy className="w-3 h-3 mr-0.5" /> Copy
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={prompts[slot.key] || ''}
                  onChange={e => { setPrompts(p => ({ ...p, [slot.key]: e.target.value })); setManual(m => ({ ...m, [slot.key]: true })); }}
                  rows={4} className="text-xs font-mono leading-relaxed"
                  placeholder="Click 'Generate All' to assemble from pool configuration..."
                  data-testid={`prompt-${slot.key}`}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
const RENDER_STATUSES = {
  not_started: { label: 'Not Started', color: 'bg-slate-100 text-slate-600' },
  prompt_ready: { label: 'Prompts Ready', color: 'bg-amber-100 text-amber-700' },
  requested: { label: 'Requested', color: 'bg-sky-100 text-sky-700' },
  in_progress: { label: 'In Progress', color: 'bg-violet-100 text-violet-700' },
  revision_requested: { label: 'Revision Requested', color: 'bg-red-100 text-red-700' },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
};

function RenderWorkflow({ bid, bidId, loadBid }) {
  const [revNotes, setRevNotes] = useState(bid?.render_revision_notes || '');
  const [intNotes, setIntNotes] = useState(bid?.render_internal_notes || '');

  useEffect(() => {
    setRevNotes(bid?.render_revision_notes || '');
    setIntNotes(bid?.render_internal_notes || '');
  }, [bid?.render_revision_notes, bid?.render_internal_notes]);

  const status = bid?.render_status || 'not_started';
  const version = bid?.render_version || 1;
  const statusInfo = RENDER_STATUSES[status] || RENDER_STATUSES.not_started;

  // Check if prompts exist
  const hasPrompts = bid?.render_prompts && (bid.render_prompts.concept_plan || bid.render_prompts.daytime_render || bid.render_prompts.evening_render);
  // Check if design images exist (reuse line_items-adjacent data from bid)
  const [hasImages, setHasImages] = useState(false);
  useEffect(() => {
    api.get(`/bids/${bidId}/design-images`).then(r => setHasImages(r.data.length > 0)).catch(() => {});
  }, [bidId, status]);

  const transition = async (newStatus, extra = {}) => {
    const updates = { render_status: newStatus, ...extra };
    await api.put(`/bids/${bidId}`, updates);
    loadBid();
    toast.success(`Status → ${RENDER_STATUSES[newStatus]?.label}`);
  };

  const handleMarkReady = () => transition('prompt_ready');
  const handleRequest = () => transition('requested', { render_requested_by: bid?.inputs?.customer_name || 'Admin', render_requested_at: new Date().toISOString() });
  const handleInProgress = () => {
    // If coming from revision_requested, increment version
    const extra = status === 'revision_requested' ? { render_version: version + 1 } : {};
    transition('in_progress', extra);
  };
  const handleRevision = () => {
    if (!revNotes.trim()) { toast.error('Please enter revision notes before requesting a revision'); return; }
    transition('revision_requested', { render_revision_notes: revNotes });
  };
  const handleApprove = () => {
    if (!hasImages) { toast.error('Upload at least one design image before approving'); return; }
    transition('approved', { render_completed_at: new Date().toISOString() });
  };

  const saveNotes = async () => {
    await api.put(`/bids/${bidId}`, { render_internal_notes: intNotes });
    toast.success('Notes saved');
  };

  return (
    <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-6" data-testid="render-workflow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Compass className="w-4 h-4 text-[#F59E0B]" />
            <p className="text-sm font-bold text-[#1e3a50]">Render Workflow</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-slate-400">v{version}</span>
            <Badge className={`text-[10px] ${statusInfo.color}`}>{statusInfo.label}</Badge>
          </div>
        </div>

        {/* Status details */}
        {bid?.render_requested_by && (
          <p className="text-[10px] text-slate-400 mb-2">Requested by {bid.render_requested_by} {bid.render_requested_at ? `· ${new Date(bid.render_requested_at).toLocaleDateString()}` : ''}</p>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mb-3">
          {status === 'not_started' && (
            <Button size="sm" variant="outline" className="text-xs" onClick={handleMarkReady} disabled={!hasPrompts} data-testid="mark-ready-btn">
              Mark Prompts Ready
            </Button>
          )}
          {status === 'prompt_ready' && (
            <Button size="sm" className="text-xs bg-sky-500 hover:bg-sky-600 text-white" onClick={handleRequest} data-testid="request-render-btn">
              Request Render
            </Button>
          )}
          {(status === 'requested' || status === 'revision_requested') && (
            <Button size="sm" className="text-xs bg-violet-500 hover:bg-violet-600 text-white" onClick={handleInProgress} data-testid="mark-in-progress-btn">
              Mark In Progress
            </Button>
          )}
          {status === 'in_progress' && (
            <>
              <Button size="sm" variant="outline" className="text-xs border-red-200 text-red-600" onClick={handleRevision} data-testid="request-revision-btn">
                Request Revision
              </Button>
              <Button size="sm" className="text-xs bg-emerald-500 hover:bg-emerald-600 text-white" onClick={handleApprove} disabled={!hasImages} data-testid="approve-render-btn">
                Approve
              </Button>
            </>
          )}
          {status === 'approved' && (
            <Button size="sm" variant="outline" className="text-xs border-amber-200 text-amber-600" onClick={handleRevision} data-testid="new-revision-btn">
              Request New Revision
            </Button>
          )}
        </div>

        {/* Revision notes (shown when relevant) */}
        {(status === 'in_progress' || status === 'revision_requested' || status === 'approved') && (
          <div className="mb-3">
            <Label className="text-[10px] font-bold text-slate-500">Revision Notes</Label>
            <Textarea value={revNotes} onChange={e => setRevNotes(e.target.value)} rows={2} className="text-xs mt-1"
              placeholder="Describe what needs to change..." data-testid="revision-notes" />
          </div>
        )}

        {/* Internal notes */}
        <div>
          <Label className="text-[10px] font-bold text-slate-500">Internal Notes</Label>
          <Textarea value={intNotes} onChange={e => setIntNotes(e.target.value)} onBlur={saveNotes} rows={2} className="text-xs mt-1"
            placeholder="Internal tracking notes..." data-testid="internal-notes" />
        </div>

        {/* Safety hints */}
        {status === 'not_started' && !hasPrompts && (
          <p className="text-[9px] text-amber-600 mt-2">Generate prompts first to mark as ready.</p>
        )}
        {status === 'in_progress' && !hasImages && (
          <p className="text-[9px] text-amber-600 mt-2">Upload at least one design image to approve.</p>
        )}
      </CardContent>
    </Card>
  );
}

